<template>
  <div>
    <!--
      ============================
      Body parts
      ============================
    -->
    <b-row>
      <b-col class="pb-2">
        <h5 class="bold">
          {{ $t('FILTER_bodyparts') }}
          <i
            v-if="filteredPlaylists.type === 'some'"
            v-b-popover.hover.top="$t('PAGE_home_filter_q1_some_mode')"
            class="icon fa fa-info-circle pl-2"
            aria-hidden="true"
          />
        </h5>
      </b-col>
    </b-row>
    <b-row no-gutters class="hack-margin">
      <b-col
        cols="6"
        sm="4"
        lg="3"
        xl="2"
        class="pb-3 pr-3"
        v-for="part in parts"
        :key="part.name"
      >
        <b-button
          variant="outline-primary"
          :class="[
            'button',
            {
              'button-active': filter.area.includes(part.name),
            },
          ]"
          @click="
            $store.dispatch('setFilter', {
              area: filter.area.includes(part.name)
                ? filter.area.filter(r => r !== part.name)
                : [...filter.area, part.name],
            })
          "
        >
          <div
            :class="`image-wrapper ${isMobile ? 'image-wrapper-mobile' : ''}`"
          >
            <body-part-image :part="part" class="image w-100 h-100" />
          </div>

          <div class="name">{{ $t(`bodypart_${part.name}`) }}</div>
        </b-button>
      </b-col>
    </b-row>

    <!--
      ============================
      duration
      ============================
    -->
    <b-row>
      <b-col class="pb-2">
        <h5 class="bold">{{ $t('FILTER_duration') }}</h5>
      </b-col>
    </b-row>
    <b-row no-gutters class="hack-margin">
      <b-col
        cols="12"
        sm="4"
        class="pb-3 pr-3"
        v-for="duration in durations"
        :key="duration.upper"
      >
        <b-button
          variant="outline-primary"
          :class="[
            'button',
            {
              'button-active': (filter.duration || {}).lower === duration.lower,
            },
          ]"
          @click="
            $store.dispatch('setFilter', {
              duration:
                (filter.duration || {}).lower === duration.lower
                  ? null
                  : duration,
            })
          "
        >
          {{ $t(`PAGE_home_filter_q2_${duration.lower}_${duration.upper}`) }}
        </b-button>
      </b-col>
    </b-row>

    <!--
      ============================
      trainings dimension
      ============================
    -->
   <b-row>
      <b-col class="pb-2">
        <h5 class="bold">{{ $t('FILTER_dimension') }}</h5>
      </b-col>
    </b-row>
    <b-row no-gutters class="hack-margin">
      <b-col
        cols="3"
        sm="3"
        class="pb-3 pr-3"
        v-for="dimension in dimensions"
        :key="dimension"
      >
        <b-button
          variant="outline-primary"
          :class="[
            'button',
            {
              'button-active': filter.dimensions.includes(dimension),
            },
          ]"
          @click="
            $store.dispatch('setFilter', {
              dimensions: filter.dimensions.includes(dimension)
                ? filter.dimensions.filter(r => r !== dimension)
                : [...filter.dimensions, dimension],
            })
          "
        >
          <div class="name">{{ $t(`dimension_${dimension}`) }}</div>
        </b-button>
      </b-col>
    </b-row>

    <!--
      ============================
      intensity
      ============================
    -->
    <b-row>
      <b-col class="pb-2">
        <h5 class="bold">{{ $t('FILTER_intensity') }}</h5>
      </b-col>
    </b-row>
    <b-row no-gutters class="hack-margin">
      <b-col
        cols="4"
        sm="4"
        class="pb-3 pr-3"
        v-for="intensity in intensities"
        :key="intensity"
      >
        <b-button
          variant="outline-primary"
          :class="[
            'button',
            {
              'button-active': filter.intensity === intensity,
            },
          ]"
          @click="
            $store.dispatch('setFilter', {
              intensity: filter.intensity === intensity ? null : intensity,
            })
          "
          >{{ $t(`PAGE_home_filter_q4_${intensity}`) }}</b-button
        >
      </b-col>
    </b-row>

    <!--
      ============================
      reset button
      ============================
    -->
    <b-row align-v="center" class="pt-3">
      <b-col>
        {{ $t('PLAYLIST_FILTER_number_playlists_found', {number: filteredPlaylists.playlists.length})  }}
      </b-col>
      <b-col>
        <b-button
          :variant="hasFilter ? 'outline-danger' : 'outline-secondary'"
          class="float-right"
          @click="$store.dispatch('resetFilter')"
          >{{ $t('PAGE_home_filter_resetbutton') }}</b-button
        >
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import BodyPartImage from '@/elements/BodyPartImage';
import config from '@/config'

export default {
  name: 'playlist-filter',
  components: {
    BodyPartImage,
  },
  computed: {
    ...mapState(['filter']),
    ...mapGetters(['isMobile', 'filteredPlaylists', 'hasFilter']),
    parts() {
      const parts = ['body', 'hip', 'back', 'shoulder', 'legs', 'neck'];

      return parts.map(part => ({ name: part, active: false }));
    },
    durations() {
      return [
        { lower: 0, upper: 10 },
        { lower: 10, upper: 20 },
        { lower: 20, upper: false },
      ];
    },
    intensities() {
      return [1, 2, 3];
    },
    dimensions() {
      return config.availableDimensions
    }
  },
};
</script>

<style lang="scss" scoped>
@import 'node_modules/bootstrap/scss/functions';
@import 'node_modules/bootstrap/scss/bootstrap';
@import '/../theme';

.image-wrapper {
  display: inline-block;
  margin: 2px 0 0;
  height: 40px;
  width: 40px;
  line-height: 44px;
}

.image {
  background-color: theme-color('grey');
}

.image-wrapper-mobile {
  margin-top: 4px;
  height: 50px;
  width: 50px;
  line-height: 55px;
}

.button-active {
  color: theme-color('primary') !important;
  border-color: theme-color('primary') !important;
  border-width: 2px !important;
  padding: 4px !important;

  & .image {
    background-color: theme-color('primary');
  }

  &:hover {
    color: white !important;
  }
}

.name {
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}
.button {
  padding: 5px;
  width: 100%;
  color: theme-color('grey');
  border: 1px solid theme-color('light2');

  &:hover {
    background-color: theme-color-level('primary', 3);
    color: white;

    & .image {
      background-color: white;
    }
  }
}

.bold {
  font-weight: bold;
}

.hack-margin {
  margin-right: -15px;
}
</style>



// WEBPACK FOOTER //
// src/components/PlaylistFilter.vue